import { z } from "zod"
import { EntitlementsExtras } from "./entitlements"

export namespace UserExtras {
	export const AUTH_COOKIE_KEY = "_vidsup_auth"

	export const account_types = {
		influencer: 1,
		individual: 0,
	} as const

	export const profile_pic_schema = z
		.object({
			small: z.string().nullish(),
			medium: z.string().nullish(),
			original: z.string().nullish(),
		})
		.nullable()

	export const follow_status_schema = z.union([
		z.literal(0),
		z.literal(1),
		z.literal(2),
		z.literal(3),
	])

	export function planFromEntitlements(
		entitlements: string[],
	): "free" | "basic" | "advanced" {
		if (
			entitlements.includes(
				EntitlementsExtras.plan_advanced,
			)
		) {
			return "advanced"
		}

		if (
			entitlements.includes(EntitlementsExtras.plan_basic)
		) {
			return "basic"
		}

		return "free"
	}

	export const track_action_option = [
		"notif",
		"wallet_tr",
	] as const

	function cleanupSingleDeviceInfo(
		obj: any,
		current_user = false,
	) {
		if (!obj) return obj
		if (obj.is_private && !current_user) {
			obj.socials = []
		}
		if (obj["device"]) {
			if (!current_user) {
				delete obj.device
				delete obj.email
				delete obj.is_pro
				delete obj.intro_passed
				delete obj.version
				delete obj.region
				delete obj.is_anonymous
				delete obj.tiktok
				delete obj.snap
				delete obj.youtube
				delete obj.installed_count
				delete obj.save_tutorials_count
				delete obj.push_id
				delete obj.device_id
				delete obj.phone_number
				delete obj.activity_open_time
				delete obj.last_open_time
				delete obj.last_closed_time
				delete obj.tmp_uid
				delete obj.app_opened_count
				delete obj.media_count
				delete obj.shoot_tutorials_count
				delete obj.is_promocode_active
				delete obj.track_user_id
				delete obj.logged_in
				delete obj.platform
				delete obj.pro_expiry_date
				delete obj.created_at
				delete obj.updated_at
				delete obj.birthdate
				delete obj.is_fetched
				delete obj.auid
				delete obj.android_version
				delete obj.prompt_username
				delete obj.allow_sell
				delete obj.prompt_username
				delete obj.allow_posting
				delete obj.deactivated
				delete obj.deactivated_date
				delete obj.deactivate_reason
				delete obj.delete_reason
				delete obj.allow_posting_material
				delete obj.onboarding_qa
				delete obj.verified_email
			} else {
				let device = obj["device"]
				if (typeof device == "string") {
					device = JSON.parse(device)
				}
				obj["device"] = JSON.stringify({
					disabled_notifications:
						device["disabled_notifications"],
				})

				if (device["disabled_notifications"]) {
					obj["device_info"] = {
						disabled_notifications:
							device["disabled_notifications"],
					}
				}
			}
		}
		return obj
	}

	const follow_state = {
		follow: 1,
		unfollow: -1,
		requested: 0,
	} as const

	const follow_statuses = {
		not_following: 0,
		following: 1,
		follow_back: 2,
		requested: 3,
	} as const

	export type FollowStatus =
		(typeof follow_statuses)[keyof typeof follow_statuses]

	interface UserFollowStatus {
		// Other user follows self
		follower_state: number | null

		// You follow other user
		following_state: number | null

		is_follow_back?: boolean
		follow_status?: FollowStatus

		pending_follow_request?: boolean
	}

	export // function convertSingleFollowStatus(obj) {
	// 	let followStatus = 0;
	// 	if (obj.following_state  == null || obj.following_state == -1) {
	// 		if (obj.follower_state == null || obj.follower_state == 0 || obj.follower_state == -1) {
	// 			followStatus = 0;
	// 		} else {
	// 			followStatus = 2;
	// 		}
	// 	} else if (obj.following_state == 0) {
	// 		followStatus = 3;
	// 	} else {
	// 		followStatus = 1;
	// 	}

	// 	obj.is_follow_back = obj.follower_state == 1;
	// 	obj.follow_status = followStatus;
	// 	obj.pending_follow_request = obj.follower_state == 0;
	// 	return obj
	// }

	function parseFollowStatuses<
		T extends {
			uid: string
			following_state: number | null
			follower_state: number | null
		},
	>(user_states: T[]) {
		return user_states.map((user_state) => {
			return {
				...user_state,
				follow_status: parseSingleFollowStatus(
					user_state.following_state,
					user_state.follower_state,
				),
			}
		})
	}

	export function parseSingleFollowStatus(
		following_state: number | null,
		follower_state: number | null,
	): FollowStatus {
		let follow_status: FollowStatus = 0
		if (following_state == null || following_state == -1) {
			if (
				follower_state == null ||
				follower_state == follow_state.requested ||
				follower_state == follow_state.unfollow
			) {
				follow_status = follow_statuses.not_following
			} else {
				follow_status = follow_statuses.follow_back
			}
		} else if (following_state == 0) {
			follow_status = follow_statuses.requested
		} else {
			follow_status = follow_statuses.following
		}
		return follow_status
	}

	function convertSingleFollowStatus<T extends object>(
		current: UserFollowStatus & T,
	): UserFollowStatus & T {
		let follow_status: FollowStatus = 0
		if (
			current.following_state == null ||
			current.following_state == -1
		) {
			if (
				current.follower_state == null ||
				current.follower_state == follow_state.requested ||
				current.follower_state == follow_state.unfollow
			) {
				follow_status = follow_statuses.not_following
			} else {
				follow_status = follow_statuses.follow_back
			}
		} else if (current.following_state == 0) {
			follow_status = follow_statuses.requested
		} else {
			follow_status = follow_statuses.following
		}

		if (current.follower_state == 0) {
			current.pending_follow_request = true
		}

		current.is_follow_back =
			current.follower_state == follow_state.follow
		current.follow_status = follow_status
		return current
	}

	export function convertToFollowStatus(result: any[]) {
		result.forEach((part, index, theArray) => {
			theArray[index] = convertSingleFollowStatus(
				cleanupSingleDeviceInfo(theArray[index]),
			)
		})
		return result
	}

	export function validateAndCleanupUser(
		user: any,
		current_user: boolean,
	) {
		return cleanupSingleDeviceInfo(user, current_user)
	}

	export function convertToProfileLink(options: {
		username: string
	}) {
		const { username } = options
		return "https://vidsup.com/" + username
	}
	function getRandomArbitrary(min: number, max: number) {
		return Math.floor(Math.random() * (max - min) + min)
	}
	export function makeuid() {
		const length = getRandomArbitrary(14, 16)
		let result = ""
		const characters = "0123456789abcdefg"
		const charactersLength = characters.length
		for (let i = 0; i < length; i++) {
			result += characters.charAt(
				Math.floor(Math.random() * charactersLength),
			)
		}
		return result
	}

	export function checkKidModeActive(
		kidmode?: string | null,
	) {
		return (
			kidmode != null &&
			typeof kidmode !== "undefined" &&
			kidmode != ""
		)
	}

	export const user_notes_schema = z.object({
		notes: z.string().array(),
	})

	export const user_device_data_schema = z.object({
		os_version: z.string(),
		app_version: z.string(),
		device_name: z.string(),
		providers: z.string().array().optional().default([]),
	})
}
